import { CircularProgress } from '@mui/material'
import { type FC, type ReactElement, useEffect, useState } from 'react'
import { useAppSelector } from '../../../../../shared/hooks'
import TextFieldBig from '../../../../atoms/TextFieldBig'
import type { TExportContentProps } from '../../types.ts'
import ExportContent from '../ExportContent/ExportContent.tsx'
import ExportStep from '../ExportStep/ExportStep.tsx'

const ExportFromMyConnections: FC<TExportContentProps> = ({
	contactsCount,
	option,
	setContactsCount,
	setDataToExport
}: TExportContentProps): ReactElement => {
	const { appExportConnector } = useAppSelector(state => state.app)
	const profilesExport = appExportConnector?.profilesExport
	const [isChecking, setIsChecking] = useState(false)
	const [error, setError] = useState<string>('')

	const { placeholder = '', title = '', url = '' } = option

	const showError = !!error && !isChecking

	useEffect(() => {
		const fetchCount = async () => {
			try {
				setIsChecking(true)
				setError('')

				const count = await profilesExport?.linkedInMyConnections.count('')

				setError('')
				setContactsCount(typeof count === 'number' ? count : null)
				setDataToExport('myConnections')
			} catch (e) {
				if (e instanceof Error) {
					setError(e.message)
				} else {
					setError('Something went wrong, please try again')
				}
				setContactsCount(null)
				setDataToExport(null)
			} finally {
				setIsChecking(false)
			}
		}

		void fetchCount()
	}, [profilesExport, setContactsCount, setDataToExport])

	return (
		<ExportStep title={title}>
			<ExportContent contactsCount={contactsCount} option={option}>
				<TextFieldBig
					autoComplete='new-password'
					endAdornment={isChecking ? <CircularProgress size='24px' /> : null}
					error={showError}
					helperText={showError ? error : ''}
					onChange={() => {}}
					placeholder={placeholder}
					readOnly={true}
					sx={{
						'&.MuiTextField-root': {
							'& .MuiInputBase-root': {
								'& .MuiOutlinedInput-notchedOutline': {
									borderWidth: '1px',
									borderColor: 'var(--primary)'
								},
								'&:not(.Mui-error):hover .MuiOutlinedInput-notchedOutline': {
									borderColor: 'var(--primary)'
								},
								'& .MuiInputBase-input': {
									userSelect: 'none',
									WebkitUserSelect: 'none',
									MozUserSelect: 'none',
									msUserSelect: 'none',
									pointerEvents: 'none',
									cursor: 'default'
								}
							},
							width: '100%'
						},
						'.MuiFormHelperText-root.Mui-error': {
							color: 'var(--red-indicator)',
							fontSize: '12px',
							fontWeight: 600,
							mx: 0,
							textAlign: 'left'
						}
					}}
					value={url}
				/>
			</ExportContent>
		</ExportStep>
	)
}

export default ExportFromMyConnections
